(ns io.daskul.webapp.widgets.fsm.transition-logic
  "Pure FSM-transition rendering logic (SMS-122).

   Filters `:ui {:hidden true}` transitions, promotes the `:ui {:variant
   \"primary\"}` transition to the front, and humanizes unlabeled transitions so
   a renderer never falls back to a raw event id (e.g. `schedule-trial`).

   Carries no UI dependency so it runs under the webapp-ui `:node-test` target
   (which cannot load the browser-only `io.daskul.webapp.ui` / `@tabler` icon
   modules). The JSX renderer (`widgets/fsm/transitions.cljs`) consumes the
   prepared vector and stays thin. Mirrors the SMS-106 pure/layout split
   (see `widgets/form/advanced_fields.cljs`) and the framework humanize fallback
   at `aggregates/history.clj`."
  (:require
   [clojure.string :as str]))

(defn humanize-event
  "Convert a kebab-case event id (keyword or string) to a human-readable label.
   \"schedule-trial\" -> \"Schedule trial\". Mirrors framework history.clj humanize."
  [event]
  (-> (name event)
      (str/replace "-" " ")
      (str/capitalize)))

(defn visible?
  "True unless the transition is flagged `:ui {:hidden true}`."
  [transition]
  (not (boolean (get-in transition [:ui :hidden]))))

(defn primary?
  "True when the transition declares `:ui {:variant \"primary\"}`."
  [transition]
  (= "primary" (get-in transition [:ui :variant])))

(defn ensure-label
  "Guarantee a `[:ui :label]`: when none is set, humanize the event id so a
   renderer never shows the raw event id."
  [transition]
  (cond-> transition
    (str/blank? (str (get-in transition [:ui :label])))
    (assoc-in [:ui :label] (humanize-event (:event transition)))))

(defn prepare-transitions
  "Prepare a raw transitions vector for rendering:
     1. drop `:ui {:hidden true}` transitions (excluded from primary AND the
        \"More actions\" set);
     2. move the first `:ui {:variant \"primary\"}` transition to the front so
        the renderer's `(first transitions)` is the primary action (fallback:
        keep order — the first visible transition is primary);
     3. ensure every transition has a humanized `[:ui :label]` fallback.

   Order within the non-primary group is preserved. Nil/empty input yields `[]`."
  [transitions]
  (let [visible (filterv visible? transitions)
        primary-event (some #(when (primary? %) (:event %)) visible)
        ordered (if primary-event
                  (let [{primaries true others false}
                        (group-by #(= primary-event (:event %)) visible)]
                    (vec (concat primaries others)))
                  visible)]
    (mapv ensure-label ordered)))

;; ---------------------------------------------------------------------------
;; Record-header presentation — three independent axes (F-061, #694)
;;
;;   :emphasis     :primary | :secondary | :normal    how loud
;;   :placement    :bar | :menu                       where — usually derived
;;   :destructive? true                               what kind
;;
;; An earlier draft folded placement into emphasis as `:emphasis :menu`. That
;; is a category error, and the counterexample is routine: "Mark lost" is a
;; red-text item in the header menu AND the red-filled primary confirm inside
;; its own drawer. Same transition, two treatments, both true at once — one
;; enum forces a choice and will be wrong in one place.
;; ---------------------------------------------------------------------------

(def max-bar-buttons
  "A cap, not a quota. A state with one legal transition shows one button and
   no menu. Two rather than one is deliberate: most Lead states have two
   genuine next steps (schedule the trial OR defer), and burying one makes the
   common path a two-click path."
  2)

(def ^:private emphasis-rank {:primary 0 :secondary 1 :normal 2})

(defn emphasis
  "Declared `:ui {:emphasis …}`, else derived from the legacy `:ui {:variant}`,
   else :normal. An unrecognised value degrades to :normal rather than throwing
   — a typo in a definition should not blank the header.

   The legacy fallback matters more than it looks: NO definition declares the
   new keys yet, so without it every transition renders :normal and the header
   loses its primary action entirely. Same migration shape as :role -> :tone
   in #679 — read the old spelling until the definitions catch up."
  [transition]
  (let [declared (some-> (get-in transition [:ui :emphasis]) keyword)
        variant  (some-> (get-in transition [:ui :variant]) name)]
    (cond
      (contains? emphasis-rank declared) declared
      (= "primary" variant) :primary
      (= "secondary" variant) :secondary
      :else :normal)))

(defn destructive?
  "Declared `:ui {:destructive? true}`, else the legacy `:variant \"danger\"`.
   This is about what the action DOES, not how loud it is or where it sits —
   which is why it is a separate key from :emphasis rather than a value of it."
  [transition]
  (boolean (or (get-in transition [:ui :destructive?])
               (= "danger" (some-> (get-in transition [:ui :variant]) name)))))

(defn confirm-button-props
  "Mantine props for the button that COMMITS a transition — the one inside a
   slide-over, drawer, or confirm modal.

   Always full emphasis (Mantine's default filled variant), because a form's
   commit button is never one of several competing choices: by the time it is
   on screen the user has already chosen, and there is nothing left for it to
   be quieter than. This is why it does NOT read `:ui :variant` the way the
   choose-a-transition buttons do — `:variant \"secondary\"` correctly makes
   \"Join an Existing Session\" the quiet option in the record header, and
   just as correctly must not gray out the button that performs it (#792).

   Destructive is the one thing that survives the trip, in red rather than
   brand: it describes what the click DOES, not how loud it is, and the
   irreversible click is the last place to drop that signal."
  [transition]
  (if (destructive? transition) {:color "red"} {}))

(defn placement
  "Where the transition renders: :bar or :menu.

   Derived unless the author disagrees. Destructive actions default to the
   menu, so the header bar is nearly always violet + neutral and red appears
   only once you have opened the thing that asks 'are you sure'. Red before a
   decision is a warning; red after it is a threat.

   Write `:placement :bar` only to disagree — e.g. `Cancel session`, where the
   destructive action genuinely is a main path."
  [transition]
  (let [declared (some-> (get-in transition [:ui :placement]) keyword)]
    (cond
      (#{:bar :menu} declared) declared
      (destructive? transition) :menu
      :else :bar)))

(defn split-for-header
  "Split prepared transitions into `{:bar [...] :menu [...]}`.

   Bar candidates sort by emphasis (declaration order breaks ties, so a
   definition that declares nothing still renders in a sensible order), then
   everything past `max-bar-buttons` overflows into the menu. Menu order keeps
   the original declaration order so the overflow reads predictably.

   Takes the output of `prepare-transitions` — hidden ones are already gone."
  [transitions]
  (let [indexed (map-indexed vector transitions)
        {bar-candidates true menu-declared false}
        (group-by (fn [[_ t]] (= :bar (placement t))) indexed)
        sorted (sort-by (fn [[i t]] [(emphasis-rank (emphasis t)) i]) bar-candidates)
        [in-bar overflow] (split-at max-bar-buttons sorted)
        menu (->> (concat menu-declared overflow)
                  (sort-by first)
                  (mapv second))]
    {:bar (mapv second in-bar)
     :menu menu}))

;; ---------------------------------------------------------------------------
;; Container resolution (F-061, #696)
;;
;; The container derives from the KIND of input a transition asks for, never
;; from a per-transition allowlist. Adding a transition means declaring its
;; fields; nobody has to remember to register it somewhere else.
;; ---------------------------------------------------------------------------

(def ^:private roomy-widgets
  "Widgets that need vertical room: a date grid, a time picker, a dropdown
   list. Matched on the declared widget first because that is the authored
   intent; :schema is only a fallback for definitions that declare none."
  #{"select" "multiselect" "date" "datetime" "time" "daterange" "lookup"})

(defn- named->str
  "`name` throws on anything that is not Named, and a schema is very often not.
   Schemas arrive from the API as JSON, so what looks like `[:maybe :string]`
   in the definition reaches us as `[\"maybe\" \"string\"]`."
  [x]
  (when (or (keyword? x) (symbol? x) (string? x))
    (str/lower-case (name x))))

(defn schema-type
  "The type word of a malli schema, or nil when it has none.

   A schema is either named (`:uuid`) or a vector (`[:maybe :string]`), and
   calling `name` on the vector form throws — the crash this replaces, which
   blanked the page whenever a transition with an optional param opened its
   drawer.

   `:maybe` is unwrapped rather than read, because optionality says nothing
   about how much room a field wants: `[:maybe :date]` is still a date."
  [schema]
  (if (sequential? schema)
    (let [head (named->str (first schema))]
      (if (= "maybe" head)
        (schema-type (second schema))
        head))
    (named->str schema)))

(defn roomy-field?
  "True when a param field needs the wider drawer. Reads `:ui {:widget …}`,
   falling back to the schema for definitions that declare no widget — a
   `uuid` param is a lookup in practice, and `date` is a date."
  [{:keys [ui schema]}]
  (let [widget (some-> (:widget ui) named->str)
        schema-name (schema-type schema)]
    (boolean
     (or (contains? roomy-widgets widget)
         (and (nil? widget)
              (contains? #{"uuid" "date" "datetime" "time"} schema-name))))))

(defn required-field?
  "`extract-param-fields` sets :required from (not :optional)."
  [field]
  (boolean (:required field)))

(defn container
  "`:drawer` or `:popover` for a transition and its extracted param fields.

   Any REQUIRED param means the transition asks you for data, so it gets a
   drawer. Nothing required means it only wants confirmation, so it gets a
   popover anchored to its own button.

   Counting fields was considered and rejected: adding a third parameter would
   silently flip the same button from popover to drawer, and nobody could
   predict which they would get before clicking. Splitting by KIND means the
   container answers a question the user can already perceive — does this ask
   me for data?

   An explicit `:ui {:container …}` wins; that is the escape hatch for a
   transition whose drawer opens something the field list cannot describe."
  [transition fields]
  (let [declared (some-> (get-in transition [:ui :container]) keyword)]
    (cond
      (#{:drawer :popover} declared) declared
      (some required-field? fields) :drawer
      :else :popover)))

(defn nested-confirm?
  "Whether a panel hosted in CONTAINER renders `:ui {:confirm …}` as a SECOND,
   modal step.

   A popover is ALREADY the confirmation. It asks nothing but \"are you sure\",
   and its own buttons are the answer — so a modal inside one is a second ask
   for the same decision, and a broken one. Mantine dismisses a popover from a
   document-level `mousedown` scoped to its target and dropdown, and a modal is
   portalled to the body, so it counts as outside: the mousedown that pressed
   Confirm unmounted the popover and took the modal with it, and the click
   never landed. That is why a draft Program could not be activated (#810).

   A drawer holds a form, so a final are-you-sure IS a distinct step there —
   and a drawer closes from its own overlay rather than a document listener, so
   the same nesting is safe. The inline panel keeps it for the same reason."
  [container transition]
  (and (some? (get-in transition [:ui :confirm]))
       (not= :popover container)))

(defn panel-copy
  "The copy the expanded panel shows above its fields, in order.

   The `:confirm` message joins it only where the container hosts the
   confirmation itself (see `nested-confirm?`) — otherwise the modal still
   carries it, and saying it in both places states the same thing twice, a
   line apart.

   Values pass through as authored — a string, or the `{:message … :variant …}`
   alert map — so the renderer keeps deciding how to draw each one. The
   `Confirm <label>?` fallback appears only when a transition says nothing at
   all about itself."
  [container transition]
  (let [described (get-in transition [:ui :description])
        message (when-not (nested-confirm? container transition)
                  (get-in transition [:ui :confirm :message]))]
    (or (not-empty (filterv some? [described message]))
        [(str "Confirm " (or (get-in transition [:ui :label]) (:event transition)) "?")])))

(def drawer-width-narrow 400)
(def drawer-width-wide 460)

(defn drawer-width
  "Width in px, from content rather than from a per-transition setting: more
   than two roomy fields earns the wider drawer, everything else stays narrow."
  [fields]
  (if (> (count (filter roomy-field? fields)) 2)
    drawer-width-wide
    drawer-width-narrow))

;; ---------------------------------------------------------------------------
;; Record-sourced context rows (#792)
;;
;; A transition's `:ui {:context [...]}` declares read-only facts pulled off the
;; RECORD and shown above the param form. It exists for the case where a param
;; is filtered by something the form does not display: the session pickers list
;; only the Lead's own program, so repeating that program in every option is
;; noise — but dropping it entirely leaves the admin picking a time with no
;; sense of what for. Stating it once, above the field, says the same thing at
;; a tenth the width.
;; ---------------------------------------------------------------------------

(defn- resolve-context-path
  "Resolve a context row's :path against a record. Mirrors widgets/display's
   resolve-path rule: a path whose FIRST element is a vector is a compound
   template ([[:person :first] \" \" [:person :last]]); anything else is a plain
   get-in path. Keys arrive as strings after the JSON round-trip."
  [record path]
  (when (seq path)
    (if (vector? (first path))
      (let [parts (map (fn [part]
                         (if (string? part) part (mapv keyword part)))
                       path)
            resolved (map (fn [part] (if (string? part) part (get-in record part)))
                          parts)
            has-data? (some some? (map #(when-not (string? %2) %1) resolved parts))]
        (when has-data?
          (->> resolved (remove nil?) (str/join))))
      (get-in record (mapv keyword path)))))

(defn context-rows
  "Rows for a transition's `:ui {:context [{:label \"Program\" :path [...]}]}`,
   resolved against the record. Rows whose path resolves to nil/blank are
   dropped, so an unset field leaves no empty line. Returns nil when the
   transition declares no context or nothing resolves."
  [transition record]
  (when record
    (->> (get-in transition [:ui :context])
         (into [] (keep (fn [{:keys [label path]}]
                          (let [v (resolve-context-path record path)]
                            (when-not (str/blank? (str v))
                              {:label label :value (str v)})))))
         (not-empty))))

;; ---------------------------------------------------------------------------
;; Conditional param fields (#803)
;;
;; A param can declare `:ui {:visible-when {:field :create-invoice? :equals
;; true}}` — it renders only while another field in the SAME form holds that
;; value. It exists for the case where one param only means anything because
;; of another: "Bill to" is who the renewal invoice is addressed to, so with
;; "Create Invoice" off it is a question about a document that will not be
;; written.
;;
;; Deliberately not `:depends-on`, which already means something else here:
;; that disables a lookup until its parent supplies a filter, and the field
;; stays on screen because you are still expected to fill it. This removes
;; the field because there is nothing to ask.
;; ---------------------------------------------------------------------------

(defn- condition-holds?
  "One `{:field … :equals …}` clause against the current form data.

   A clause naming a field that appears NEITHER among the form's fields nor
   in its data is VACUOUS — it holds. You cannot gate on a question nobody
   was asked, and treating it as a failed match would hide the dependent
   field forever. That is the case #803 needs: a student with no linked
   guardians has the radio dropped server-side, and the search field it gates
   must then stand alone, or the drawer offers no way to name a payer at all.

   Both halves of that test matter. A field can legitimately be in the data
   without being in `present` — the enclosing form merges the record in — and
   there the clause is answerable, so it is answered.

   Keys arrive as strings after the JSON round-trip, and so does `:equals`
   when the declared value is a keyword — compare loosely enough to survive
   that, without letting nil match false."
  [{on-field :field :keys [equals]} form-data present]
  (let [k (keyword on-field)]
    (if-not (or (contains? present k) (contains? form-data k))
      true
      (let [actual (get form-data k)]
        (if (boolean? equals)
          (= equals (boolean actual))
          (= (str equals) (str actual)))))))

(defn- visible-field?
  "Does this param field's `:visible-when` hold? Fields that declare none are
   always visible.

   Accepts a single clause or a vector of them; a vector means ALL must hold.
   \"Bill to\" needs both — Create Invoice on, AND (if it was asked) the
   guardian radio set to Someone else."
  [field form-data present]
  (let [decl (get-in field [:ui :visible-when])
        clauses (cond (nil? decl) nil
                      (sequential? decl) decl
                      :else [decl])]
    (every? #(condition-holds? % form-data present) clauses)))

(defn visible-fields
  "The param fields that should render, given what the form currently holds."
  [fields form-data]
  (let [present (into #{} (map :name) fields)]
    (filterv #(visible-field? % form-data present) fields)))

(defn submitted-params
  "Form data with hidden fields dropped.

   The form KEEPS a hidden field's value so toggling the condition back on
   restores what you picked, but it must not be SUBMITTED: a stale \"Bill to\"
   travelling with Create Invoice off would name a payer the admin cannot see
   on screen, and #803 is a story about the payer being invisible."
  [fields form-data]
  (let [present (into #{} (map :name) fields)
        hidden (into #{}
                     (comp (remove #(visible-field? % form-data present))
                           (map :name))
                     fields)]
    (apply dissoc form-data hidden)))
